'use client'

/**
 * La scena del **muro** (monster block) a partire dagli eventi di una sessione.
 *
 * Stessa logica della sostituzione (`useSubstitutionScene`): il muro è un evento
 * tecnico, non cambia lo stato, quindi chi lo vuole mostrare deve leggere gli
 * **eventi**. Primo giro = segnaposto (niente scena per i muri vecchi), poi solo
 * gli eventi nuovi.
 */
import { useEffect, useRef, useState } from 'react'

export interface BlockScene {
  team: 'home' | 'away'
  /** id dell'evento: evita di animare due volte lo stesso muro. */
  id: number
}

interface Row {
  id: number
  type: string
  team: string | null
  payload: Record<string, unknown> | null
}

/** Quanto resta in onda la scena: mattoni + esplosione + testo. */
export const BLOCK_SCENE_MS = 3600

/**
 * Un muro normale **non** manda in scena i mattoni: la scena è del *monster
 * block*, cioè del muro che fa punto. Il segno viaggia nel `detail` (vedi
 * `eventForTwoFingers`: verticale + dito nel campo avversario).
 */
export function isMonsterBlockDetail(detail: unknown): boolean {
  return detail === 'monster' || detail === '++'
}

function isMonsterBlock(event: Row): boolean {
  return isMonsterBlockDetail(event.payload?.detail)
}

export function useBlockScene(events: Row[], ready: boolean): BlockScene | null {
  const [scene, setScene] = useState<BlockScene | null>(null)
  const seen = useRef(0)
  const baselineDone = useRef(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!ready) return
    if (!baselineDone.current) {
      baselineDone.current = true
      seen.current = events[events.length - 1]?.id ?? 0
      return
    }
    for (const event of events) {
      if (event.id <= seen.current) continue
      seen.current = event.id
      if (event.type !== 'block' || !isMonsterBlock(event)) continue
      const rawTeam = event.team ?? (typeof event.payload?.team === 'string' ? event.payload.team : null)
      const team = rawTeam === 'home' || rawTeam === 'away' ? rawTeam : null
      if (!team) continue
      setScene({ team, id: event.id })
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => setScene(null), BLOCK_SCENE_MS)
    }
  }, [events, ready])

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])

  return scene
}

export default useBlockScene
